import { Field, Label } from '@headlessui/react';
import { FC } from 'react';

import {
  CloseCircleIcon,
  EditIcon,
  EyeIconSmall,
  SuccessCircleIcon,
  TrashBinIcon,
} from '../../../../assets/icons/index';
import { Actions } from '../../../../components/actions/index';
import { Checkbox } from '../../../../components/checkbox/index';
import { RowPayload, TableRow } from '../../../../components/table/index';
import { Tag, TagVariant } from '../../../../components/tag/index';
import { Tooltip } from '../../../../components/tooltip/index';
import { formatDisplayValue } from '../../../../utils/format-display-value.util';
import { companyIndustryTranslation } from '../../../companies/view-company-modal/view-company-modal.types';
import { formStore } from '../form/form.store';
import { useActivatePerk } from '../hooks/use-activate-perk.hook';
import { useDeactivatePerk } from '../hooks/use-deactivate-perk.hook';
import { useDeletePerk } from '../hooks/use-delete-perk.hook';
import {
  Perk,
  perkClaimDisplayRuleTranslationKey,
  PerkClaimType,
  perkClaimTypeTranslationKey,
} from '../perks.types';
import { popupStore } from '../popup/popup.store';
import { PopupType } from '../popup/popup.types';
import { PerksColumn, perksColumnOrderMap } from '../table.types';
import { perkSelectStore } from './table.store';

export type PerksRowProps = {
  data: Perk;
};

export const PerksRow: FC<PerksRowProps> = ({ data: perk }) => {
  const { active, clicksCount, draft, industry, claimType, contactPerson, partnerName, id, logo } =
    perk;

  const openViewWithEdit = formStore(({ openViewWithEdit }) => openViewWithEdit);
  const openViewLocked = formStore(({ openViewLocked }) => openViewLocked);
  const openPopup = popupStore(({ openPopup }) => openPopup);

  const selected = perkSelectStore(({ isIdSelected }) => isIdSelected(id));
  const toggle = perkSelectStore(({ toggleId }) => toggleId);

  const { deletePerk } = useDeletePerk();
  const { deactivatePerk } = useDeactivatePerk();
  const { activatePerk } = useActivatePerk();

  const payload: RowPayload<PerksColumn> = {
    [PerksColumn.CHECKBOX]: (
      <div className="flex h-full w-full cursor-pointer items-center justify-center">
        <Field className="flex items-center">
          <Checkbox checked={selected} id={id} onChange={() => toggle(id)} />
          <Label className="absolute left-0 top-0 h-full w-full cursor-pointer" htmlFor={id} />
        </Field>
      </div>
    ),
    [PerksColumn.PARTNER]: (
      <div className="flex items-center gap-3">
        {logo && <img className="size-10 rounded-full object-cover" src={logo} />}
        <Tooltip content={partnerName}>
          <span className="max-w-[150px] truncate">{partnerName}</span>
        </Tooltip>
      </div>
    ),
    [PerksColumn.INDUSTRY]: industry ? (
      <Tooltip content={companyIndustryTranslation[industry]}>
        <span className="max-w-[150px] truncate">{companyIndustryTranslation[industry]}</span>
      </Tooltip>
    ) : (
      '-'
    ),
    [PerksColumn.TYPE]: claimType
      ? perk.claimType === PerkClaimType.PROMO_CODE
        ? perk.claimDisplayRule
          ? perkClaimTypeTranslationKey[claimType] +
            ` (${perkClaimDisplayRuleTranslationKey[perk.claimDisplayRule]})`
          : ''
        : perkClaimTypeTranslationKey[claimType]
      : '-',
    [PerksColumn.POC]: (
      <Tooltip content={contactPerson}>
        <span className="max-w-[150px] truncate">{contactPerson}</span>
      </Tooltip>
    ),
    [PerksColumn.NUMBER_OF_CLICKS]: formatDisplayValue(clicksCount),
    [PerksColumn.STATUS]: (
      <div className="flex items-center gap-3">
        {!draft && (
          <Tag
            title={active ? 'Active' : 'Inactive'}
            variant={active ? TagVariant.GREEN : TagVariant.YELLOW}
          />
        )}

        {draft && <Tag title={'Draft'} variant={TagVariant.PURPLE} />}
      </div>
    ),
    [PerksColumn.ACTIONS]: (
      <Actions
        actions={[
          {
            callback: () => openViewLocked(id),
            title: 'View',
            icon: <EyeIconSmall />,
          },
          {
            callback: () => openViewWithEdit(id),
            title: 'Edit Details',
            icon: <EditIcon />,
          },

          active
            ? {
                callback: () =>
                  openPopup({
                    type: PopupType.DEACTIVATE_PERK,
                    onAcceptCallback: () => deactivatePerk({ id }),
                  }),
                title: 'Deactivate',
                icon: <CloseCircleIcon className="size-4" iconColor="#344054" />,
              }
            : !draft
              ? {
                  callback: () =>
                    openPopup({
                      type: PopupType.ACTIVATE_PERK,
                      onAcceptCallback: () => activatePerk({ id }),
                    }),
                  title: 'Activate',
                  icon: <SuccessCircleIcon />,
                }
              : null,

          {
            callback: () =>
              openPopup({
                type: PopupType.DELETE_PERK,
                onAcceptCallback: () => deletePerk({ id }),
              }),
            title: 'Delete',
            className: 'text-fireside-500',
            icon: <TrashBinIcon iconColor="#F04438" />,
          },
        ]}
      />
    ),
    id,
  };

  return (
    <TableRow
      columnOrder={perksColumnOrderMap}
      contentClassName={{ [PerksColumn.CHECKBOX]: 'pl-3', [PerksColumn.ACTIONS]: 'justify-end' }}
      fields={payload}
      labelClassName={{
        [PerksColumn.ACTIONS]: '-translate-x-[40px]',
      }}
      tdClassName={{
        [PerksColumn.CHECKBOX]: 'w-[50px]',
        [PerksColumn.STATUS]: 'min-w-[150px]',
      }}
    />
  );
};
